import { ObjectDetector } from '@mediapipe/tasks-vision';
import { IFaceBboxNorm } from '../models/ocr';
import { PositionDocumentService } from '../services/ocr/position-document.service';

/** Top-k del loop OCR (document + passport). No subir en createFromOptions. */
export const OCR_LIVE_DETECTOR_MAX_RESULTS = 2;
const FACE_HINT_MAX_RESULTS = 3;

/**
 * Inferencia puntual sobre el JPEG que se POST-ea.
 * Sube maxResults solo durante este detect y restaura el modo del loop.
 */
export async function detectFaceBboxOnJpegDataUrl(
	detector: ObjectDetector | null | undefined,
	imageDataUrl: string,
	positionDocumentService: PositionDocumentService,
	restoreRunningMode: 'IMAGE' | 'VIDEO',
): Promise<IFaceBboxNorm | undefined> {
	if (!detector || !imageDataUrl.startsWith('data:')) {
		return undefined;
	}

	try {
		const canvas = await drawDataUrlToCanvas(imageDataUrl);
		if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
			return undefined;
		}

		await detector.setOptions({ runningMode: 'IMAGE', maxResults: FACE_HINT_MAX_RESULTS });
		try {
			const face = positionDocumentService.getFaceDetection(detector.detect(canvas)?.detections);
			if (!face?.boundingBox) {
				return undefined;
			}
			return (
				positionDocumentService.remapFaceBboxToJpeg(face.boundingBox, {
					sx: 0,
					sy: 0,
					sw: canvas.width,
					sh: canvas.height,
				}) ?? undefined
			);
		} finally {
			await detector.setOptions({
				runningMode: restoreRunningMode,
				maxResults: OCR_LIVE_DETECTOR_MAX_RESULTS,
			});
		}
	} catch {
		return undefined;
	}
}

function drawDataUrlToCanvas(imageDataUrl: string): Promise<HTMLCanvasElement | null> {
	return new Promise((resolve) => {
		const image = new Image();
		image.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = image.naturalWidth || image.width;
			canvas.height = image.naturalHeight || image.height;
			const ctx = canvas.getContext('2d');
			if (!ctx || canvas.width <= 0 || canvas.height <= 0) {
				resolve(null);
				return;
			}
			ctx.drawImage(image, 0, 0);
			resolve(canvas);
		};
		image.onerror = () => resolve(null);
		image.src = imageDataUrl;
	});
}
